import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Modal, Form, Alert, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { backendUrl } from '../utils/variables';
import { MyContext } from "../MainContext";
import Header from '../components/header_home';
import Footer from '../components/footer';
import '../components/TherapyNew.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBrain, 
  faHeartBroken, 
  faDove, 
  faHeart, 
  faGraduationCap, 
  faPray, 
  faCloudRain, 
  faChild,
  faStar,
  faUser,
  faCamera,
  faNairaSign,
  faCheck,
  faTimes,
  faArrowLeft,
  faSearch,
  faUserMd,
  faCoins,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';

const TherapyNew = () => {
  const { user } = useContext(MyContext);
  
  // Main states
  const [view, setView] = useState('main'); // main, categories, therapists, apply
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Application status states
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [checkingApplication, setCheckingApplication] = useState(false);
  
  // Therapist application states
  const [applicationForm, setApplicationForm] = useState({
    bio: '',
    photoFile: null,
    photoPreview: null,
    pricePerSession: '',
    selectedCategories: []
  });
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [applicationError, setApplicationError] = useState(null);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  
  // Session request states
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [sessionForm, setSessionForm] = useState({
    issueText: '',
    categoryId: null,
    startDateTime: '',
    duration: 1, // Default 1 hour
    totalPrice: 0
  });
  const [submittingSession, setSubmittingSession] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [sessionSuccess, setSessionSuccess] = useState(false);
  const [userBalance, setUserBalance] = useState(0);

  // Category icons mapping
  const categoryIcons = {
    'Mental Health': faBrain,
    'Trauma': faHeartBroken,
    'Grief': faDove,
    'Couple/Relationship': faHeart,
    'Academic Career': faGraduationCap,
    'Spiritual Growth': faPray,
    'Depression': faCloudRain,
    'Teens': faChild
  };

  // Function to get category icon
  const getCategoryIcon = (categoryName) => {
    const icon = categoryIcons[categoryName];
    if (icon) {
      return <FontAwesomeIcon icon={icon} size="2x" className="text-primary" />;
    }
    return <FontAwesomeIcon icon={faBrain} size="2x" className="text-primary" />;
  };

  // Function to format price display
  const formatPriceDisplay = (price) => {
    if (price === 0 || price === '0') {
      return <span className="text-success fw-bold">Free</span>;
    }
    return <span>{price.toLocaleString()} coins</span>;
  };

  // Load categories and check application status on component mount
  useEffect(() => {
    fetchCategories();
    if (user && user.user_id) {
      checkApplicationStatus();
    }
  }, [user]);

  const checkApplicationStatus = async () => {
    try {
      setCheckingApplication(true);
      const response = await fetch(`${backendUrl}/check_application_status.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: user.user_id })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setApplicationStatus(data.application);
      } else {
        console.error('Failed to check application status:', data.error);
      }
    } catch (err) {
      console.error('Error checking application status:', err);
    } finally {
      setCheckingApplication(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching categories from:', `${backendUrl}/fetch_therapy_categories.php`);
      
      const response = await fetch(`${backendUrl}/fetch_therapy_categories.php`);
      console.log('Response status:', response.status);
      
      const data = await response.json();
      console.log('Categories data:', data);
      
      if (data.success) {
        setCategories(data.categories);
        console.log('Categories set:', data.categories);
      } else {
        setError(data.error || 'Failed to load categories');
        console.error('Categories error:', data.error);
      }
    } catch (err) {
      console.error('Categories fetch error:', err);
      setError('Failed to load categories: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTherapistsByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/fetch_therapists_by_category.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category_id: categoryId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTherapists(data.therapists);
        setView('therapists');
      } else {
        setError(data.error || 'Failed to load therapists');
      }
    } catch (err) {
      setError('Failed to load therapists');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    fetchTherapistsByCategory(category.category_id);
  };

  const handleApplyAsTherapist = () => {
    setView('apply');
    setApplicationForm({
      bio: '',
      photoFile: null,
      photoPreview: null,
      pricePerSession: '',
      selectedCategories: []
    });
    setApplicationError(null);
    setApplicationSuccess(false);
  };

  const handleCategoryToggle = (categoryId) => {
    setApplicationForm(prev => {
      const newCategories = prev.selectedCategories.includes(categoryId)
        ? prev.selectedCategories.filter(id => id !== categoryId)
        : [...prev.selectedCategories, categoryId];
      
      return {
        ...prev,
        selectedCategories: newCategories.slice(0, 3) // Limit to 3 categories
      };
    });
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setApplicationError('Invalid file type. Please select a JPEG, PNG, or GIF image.');
        return;
      }
      
      // Validate file size (2MB limit for original)
      if (file.size > 2 * 1024 * 1024) {
        setApplicationError('File size too large. Please select an image smaller than 2MB.');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setApplicationForm(prev => ({
          ...prev,
          photoFile: file,
          photoPreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
      setApplicationError(null);
    }
  };

  const uploadPhoto = async () => {
    if (!applicationForm.photoFile) {
      return null;
    }
    
    const formData = new FormData();
    formData.append('photo', applicationForm.photoFile);
    formData.append('user_id', user.user_id);
    
    try {
      const response = await fetch(`${backendUrl}/upload_therapist_photo.php`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data.photo_url;
      } else {
        throw new Error(data.error || 'Failed to upload photo');
      }
    } catch (err) {
      throw new Error('Failed to upload photo: ' + err.message);
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    
    if (applicationForm.selectedCategories.length === 0) {
      setApplicationError('Please select at least one category');
      return;
    }
    
    if (applicationForm.selectedCategories.length > 3) {
      setApplicationError('You can select up to 3 categories');
      return;
    }
    
    try {
      setSubmittingApplication(true);
      setApplicationError(null);
      
      // Upload photo first if selected
      let photoUrl = '';
      if (applicationForm.photoFile) {
        photoUrl = await uploadPhoto();
      }
      
      const response = await fetch(`${backendUrl}/apply_as_therapist.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          bio: applicationForm.bio,
          photo_url: photoUrl,
          price_per_session: parseFloat(applicationForm.pricePerSession),
          categories: applicationForm.selectedCategories
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setApplicationSuccess(true);
        // Refresh application status
        await checkApplicationStatus();
        setTimeout(() => {
          setView('main');
          setApplicationSuccess(false);
        }, 2000);
      } else {
        setApplicationError(data.error || 'Failed to submit application');
      }
    } catch (err) {
      setApplicationError('Failed to submit application: ' + err.message);
    } finally {
      setSubmittingApplication(false);
    }
  };

  const calculateTotalPrice = (duration, pricePerHour) => {
    return duration * pricePerHour;
  };

  const checkUserBalance = async () => {
    try {
      console.log('User object:', user);
      console.log('Checking balance for user:', user.user_id || user.id);
      
      // Try both user_id and id fields
      const userId = user.user_id || user.id;
      if (!userId) {
        console.error('No user ID found in user object');
        return 0;
      }
      
      const response = await fetch(`${backendUrl}/wallet.php?email=${encodeURIComponent(user.email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      console.log('Balance API response:', data);
      
      if (data.success) {
        console.log('Setting balance to:', data.wallet.coins_balance);
        console.log('Balance type:', typeof data.wallet.coins_balance);
        console.log('Balance value:', data.wallet.coins_balance);
        
        // Ensure balance is a number
        const balance = parseFloat(data.wallet.coins_balance) || 0;
        console.log('Parsed balance:', balance);
        
        setUserBalance(balance);
        return balance;
      } else {
        console.error('Balance API error:', data.error || data.messages);
        return 0;
      }
    } catch (err) {
      console.error('Error checking balance:', err);
      return 0;
    }
  };

  const handleRequestSession = async (therapist) => {
    console.log('User object:', user);
    console.log('User ID being used:', user?.user_id || user?.id);
    
    setSelectedTherapist(therapist);
    const balance = await checkUserBalance();
    const totalPrice = calculateTotalPrice(1, therapist.price_per_session);
    
    setSessionForm({
      issueText: '',
      categoryId: selectedCategory.category_id,
      startDateTime: '',
      duration: 1,
      totalPrice: totalPrice
    });
    setShowSessionModal(true);
    setSessionError(null);
    setSessionSuccess(false);
  };

  const handleSessionSubmit = async (e) => {
    e.preventDefault();
    
    if (sessionForm.issueText.length < 10) {
      setSessionError('Please provide a detailed description (at least 10 characters)');
      return;
    }

    if (!sessionForm.startDateTime) {
      setSessionError('Please select a start date and time for your session');
      return;
    }

    if (sessionForm.duration < 1 || sessionForm.duration > 8) {
      setSessionError('Session duration must be between 1 and 8 hours');
      return;
    }

    // Check if user has sufficient balance (skip check for free sessions)
    if (sessionForm.totalPrice > 0 && sessionForm.totalPrice > userBalance) {
      setSessionError(`Insufficient balance. You need ${sessionForm.totalPrice} coins but have ${userBalance} coins. Please add funds to your wallet to continue.`);
      return;
    }
    
    try {
      setSubmittingSession(true);
      setSessionError(null);
      
      const response = await fetch(`${backendUrl}/create_therapy_session.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id || user.id,
          therapist_id: selectedTherapist.user_id,
          issue_text: sessionForm.issueText,
          category_id: sessionForm.categoryId,
          start_date_time: sessionForm.startDateTime,
          duration: sessionForm.duration,
          total_price: sessionForm.totalPrice
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSessionSuccess(true);
        setTimeout(() => {
          setShowSessionModal(false);
          setSessionSuccess(false);
          setView('main');
        }, 2000);
      } else {
        setSessionError(data.messages || 'Failed to create session request');
      }
    } catch (err) {
      setSessionError('Failed to create session request');
    } finally {
      setSubmittingSession(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FontAwesomeIcon key={i} icon={faStar} className="text-warning" />);
    }
    
    if (hasHalfStar) {
      stars.push(<FontAwesomeIcon key="half" icon={faStar} className="text-warning" style={{ opacity: 0.5 }} />);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FontAwesomeIcon key={`empty-${i}`} icon={faStar} className="text-muted" />);
    }
    
    return stars;
  };

  const renderMainView = () => (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col-12 text-center">
          <h2 className="mb-3">Therapy Services</h2>
          <p className="text-muted mb-4">Get professional support or join our network of therapists</p>
        </div>
      </div>
      
      <Row className="justify-content-center">
        <Col lg={8} md={10}>
          <Row>
            {/* Find a Therapist Card */}
            <Col md={4} className="mb-4">
              <Card 
                className="h-100 shadow-sm text-center"
                onClick={() => setView('categories')}
                style={{ cursor: 'pointer' }}
              >
                <Card.Body className="d-flex flex-column">
                  <div className="mb-3">
                    <FontAwesomeIcon 
                      icon={faSearch} 
                      size="3x" 
                      className="text-primary mb-3"
                    />
                  </div>
                  <h5 className="card-title">Find a Therapist</h5>
                  <p className="card-text text-muted">
                    Browse our network of qualified therapists and find the right match for your needs.
                  </p>
                  <div className="mt-auto">
                    <Button variant="primary" className="w-100">
                      Browse Categories
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            {/* My Sessions Card */}
            <Col md={4} className="mb-4">
              <Card 
                className="h-100 shadow-sm text-center"
                onClick={() => window.location.href = '/my-therapy-sessions'}
                style={{ cursor: 'pointer' }}
              >
                <Card.Body className="d-flex flex-column">
                  <div className="mb-3">
                    <FontAwesomeIcon 
                      icon={faCalendar} 
                      size="3x" 
                      className="text-info mb-3"
                    />
                  </div>
                  <h5 className="card-title">My Sessions</h5>
                  <p className="card-text text-muted">
                    Monitor your therapy requests and chat with approved therapists.
                  </p>
                  <div className="mt-auto">
                    <Button variant="info" className="w-100">
                      View Sessions
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            {/* Apply as Therapist / Manage Sessions Card */}
            <Col md={4} className="mb-4">
              {checkingApplication ? (
                <Card className="h-100 shadow-sm text-center">
                  <Card.Body className="d-flex flex-column">
                    <div className="mb-3">
                      <Spinner animation="border" variant="primary" />
                    </div>
                    <h5 className="card-title">Checking Application Status</h5>
                    <p className="card-text text-muted">
                      Please wait while we check your application status...
                    </p>
                  </Card.Body>
                </Card>
              ) : applicationStatus ? (
                applicationStatus.status === 'approved' ? (
                  <Card className="h-100 shadow-sm text-center">
                    <Card.Body className="d-flex flex-column">
                      <div className="mb-3">
                        <FontAwesomeIcon 
                          icon={faUserMd} 
                          size="3x" 
                          className="text-success mb-3"
                        />
                      </div>
                      <h5 className="card-title">Therapist Dashboard</h5>
                      <div className="mb-3">
                        <Badge bg="success" className="mb-2">
                          Approved Therapist
                        </Badge>
                      </div>
                      <p className="card-text text-muted">
                        Welcome back! Manage your therapy sessions and view your schedule.
                      </p>
                      <div className="mt-auto">
                        <Button 
                          variant="success" 
                          className="w-100"
                          onClick={() => window.location.href = '/therapist-dashboard'}
                        >
                          Manage Sessions
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ) : (
                  <Card className="h-100 shadow-sm text-center">
                    <Card.Body className="d-flex flex-column">
                      <div className="mb-3">
                        <FontAwesomeIcon 
                          icon={faUserMd} 
                          size="3x" 
                          className={applicationStatus.status === 'rejected' ? 'text-danger mb-3' : 'text-warning mb-3'}
                        />
                      </div>
                      <h5 className="card-title">Application Status</h5>
                      <div className="mb-3">
                        <Badge 
                          bg={applicationStatus.status === 'rejected' ? 'danger' : 'warning'}
                          className="mb-2"
                        >
                          {applicationStatus.status === 'pending' ? 'Under Review' : 'Rejected'}
                        </Badge>
                        {applicationStatus.status === 'rejected' && applicationStatus.rejection_reason && (
                          <div className="mt-2">
                            <small className="text-muted">
                              <strong>Reason:</strong> {applicationStatus.rejection_reason}
                            </small>
                          </div>
                        )}
                      </div>
                      <p className="card-text text-muted">
                        {applicationStatus.status === 'pending' ? 
                          'Your application is being reviewed by our team.' :
                          'Your application was not approved at this time.'}
                      </p>
                      {applicationStatus.status === 'rejected' && (
                        <div className="mt-auto">
                          <Button 
                            variant="outline-success" 
                            className="w-100"
                            onClick={() => setView('apply')}
                          >
                            Apply Again
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )
              ) : (
                <Card 
                  className="h-100 shadow-sm text-center"
                  onClick={() => setView('apply')}
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Body className="d-flex flex-column">
                    <div className="mb-3">
                      <FontAwesomeIcon 
                        icon={faUserMd} 
                        size="3x" 
                        className="text-success mb-3"
                      />
                    </div>
                    <h5 className="card-title">Apply as Therapist</h5>
                    <p className="card-text text-muted">
                      Join our network of therapists and help others on their journey to wellness.
                    </p>
                    <div className="mt-auto">
                      <Button variant="success" className="w-100">
                        Start Application
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      
      {/* Add bottom margin to prevent footer overlap */}
      <div style={{ marginBottom: '100px' }}></div>
    </div>
  );

  const renderCategoriesView = () => (
    <div className="container py-4">
      
      <div className="row mb-4">
        <div className="col-12">
          <h3>Select a Category</h3>
          <p className="text-muted">Choose the area you need support with</p>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading categories...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">
          <strong>Error loading categories:</strong> {error}
          <br />
          <small>Categories count: {categories.length}</small>
        </Alert>
      ) : categories.length === 0 ? (
        <Alert variant="warning">
          <strong>No categories found</strong>
          <br />
          <small>Please check if the database tables have been created and populated.</small>
          <br />
          <Button 
            variant="outline-primary" 
            size="sm" 
            className="mt-2"
            onClick={fetchCategories}
          >
            Retry Loading Categories
          </Button>
        </Alert>
      ) : (
        <Row>
          {categories.map((category) => (
            <Col key={category.category_id} md={6} lg={4} className="mb-4">
              <Card 
                className="h-100 shadow-sm category-card"
                style={{ cursor: 'pointer' }}
                onClick={() => handleCategorySelect(category)}
              >
                <Card.Body className="text-center">
                  <div className="mb-3">
                    <FontAwesomeIcon 
                      icon={categoryIcons[category.category_name] || faBrain} 
                      size="2x" 
                      style={{ color: category.color_code }}
                    />
                  </div>
                  <h5>{category.category_name}</h5>
                  <p className="text-muted small">{category.category_description}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      
      {/* Add bottom margin to prevent footer overlap */}
      <div style={{ marginBottom: '100px' }}></div>
    </div>
  );

  const renderTherapistsView = () => (
    <div className="container py-4">
      
      <div className="row mb-4">
        <div className="col-12">
          <h3>Therapists in {selectedCategory?.category_name}</h3>
          <p className="text-muted">Choose a therapist to start your session</p>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading therapists...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : therapists.length === 0 ? (
        <Alert variant="info">
          No therapists available in this category at the moment.
        </Alert>
      ) : (
        <Row>
          {therapists.map((therapist) => (
            <Col key={therapist.user_id} lg={6} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-start mb-3">
                    <div className="flex-shrink-0 me-3">
                      {therapist.photo_url ? (
                        <img 
                          src={`${backendUrl}/${therapist.photo_url}`} 
                          alt={therapist.display_name}
                          className="rounded-circle"
                          style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{ 
                            width: '60px', 
                            height: '60px', 
                            backgroundColor: '#48887b',
                            color: 'white',
                            fontSize: '1.5rem'
                          }}
                        >
                          {therapist.display_name?.charAt(0) || 'T'}
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <h5 className="mb-0 me-2">{therapist.display_name}</h5>
                        {therapist.is_verified && (
                          <Badge bg="success" className="me-2">
                            <FontAwesomeIcon icon={faCheck} size="sm" />
                          </Badge>
                        )}
                      </div>
                      <div className="mb-2">
                        {renderStars(therapist.rating)}
                        <span className="ms-2 text-muted">
                          ({therapist.total_reviews} reviews)
                        </span>
                      </div>
                      <div className="mb-2">
                        <Badge bg="info" className="me-1">
                          <FontAwesomeIcon icon={faCoins} className="me-1" />
                          {therapist.price_per_session === 0 ? 'Free' : `${therapist.price_per_session.toLocaleString()} coins/hour`}
                        </Badge>
                        <small className="text-muted d-none  mt-1 ">
                          {therapist.price_per_session > 0 && `(₦${(therapist.price_per_session * 100).toLocaleString()})`}
                        </small>
                        <Badge bg="info" className="me-1">
                          {therapist.total_sessions} sessions
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-muted mb-3">{therapist.bio}</p>
                  
                  <div className="mb-3">
                    <strong>Specialties:</strong>
                    <div className="mt-1">
                      {therapist.categories.map((category, index) => (
                        <Badge key={index} bg="light" text="dark" className="me-1">
                          {category}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <Button 
                    variant="primary" 
                    onClick={() => handleRequestSession(therapist)}
                    className="w-100"
                  >
                    Request Session
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      
      {/* Add bottom margin to prevent footer overlap */}
      <div style={{ marginBottom: '100px' }}></div>
    </div>
  );

  const renderApplicationView = () => (
    <div className="container py-4">
      
      <div className="row mb-4">
        <div className="col-12">
          <h3>Apply as a Therapist</h3>
          <p className="text-muted">Complete your profile to join our network</p>
        </div>
      </div>
      
      {applicationSuccess ? (
        <Alert variant="success">
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          Application submitted successfully! You can now start accepting sessions.
        </Alert>
      ) : (
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="shadow-sm">
              <Card.Body>
                <Form onSubmit={handleApplicationSubmit}>
                  {applicationError && (
                    <Alert variant="danger">{applicationError}</Alert>
                  )}
                  
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faCamera} className="me-2" />
                      Profile Photo
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={handlePhotoUpload}
                      className="mb-2"
                    />
                    {applicationForm.photoPreview && (
                      <div className="text-center mb-2">
                        <img 
                          src={applicationForm.photoPreview} 
                          alt="Preview" 
                          className="rounded"
                          style={{ 
                            maxWidth: '200px', 
                            maxHeight: '200px',
                            objectFit: 'cover'
                          }}
                        />
                        <div className="mt-2">
                          <small className="text-muted">
                            File: {applicationForm.photoFile?.name} 
                            ({(applicationForm.photoFile?.size / 1024 / 1024).toFixed(2)} MB)
                          </small>
                        </div>
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Upload a professional photo (JPEG, PNG, or GIF). Max 2MB. Will be compressed to under 700KB.
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      Professional Bio
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="Tell us about your background, experience, and approach to therapy..."
                      value={applicationForm.bio}
                      onChange={(e) => setApplicationForm(prev => ({
                        ...prev,
                        bio: e.target.value
                      }))}
                      required
                    />
                    <Form.Text className="text-muted">
                      Minimum 50 characters. Describe your qualifications and approach.
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>
                    <FontAwesomeIcon icon={faCoins} className="text-primary mb-2" />
                      Price per Session (10 coins = 1000 Naira)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="50.00"
                      value={applicationForm.pricePerSession}
                      onChange={(e) => setApplicationForm(prev => ({
                        ...prev,
                        pricePerSession: e.target.value
                      }))}
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>
                      <FontAwesomeIcon icon={faBrain} className="me-2" />
                      Specialties (Select up to 3)
                    </Form.Label>
                    {loading ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <p className="mt-2 text-muted small">Loading categories...</p>
                      </div>
                    ) : error ? (
                      <Alert variant="danger" className="mb-3">
                        <strong>Error loading categories:</strong> {error}
                        <br />
                        <small>Categories count: {categories.length}</small>
                      </Alert>
                    ) : categories.length === 0 ? (
                      <Alert variant="warning" className="mb-3">
                        <strong>No categories found</strong>
                        <br />
                        <small>Please check if the database tables have been created and populated.</small>
                        <br />
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="mt-2"
                          onClick={fetchCategories}
                        >
                          Retry Loading Categories
                        </Button>
                      </Alert>
                    ) : (
                      <div className="row">
                        {categories.map((category) => (
                          <Col key={category.category_id} md={6} className="mb-2">
                            <Form.Check
                              type="checkbox"
                              id={`category-${category.category_id}`}
                              label={
                                <span>
                                  <FontAwesomeIcon 
                                    icon={categoryIcons[category.category_name] || faBrain} 
                                    className="me-2"
                                    style={{ color: category.color_code }}
                                  />
                                  {category.category_name}
                                </span>
                              }
                              checked={applicationForm.selectedCategories.includes(category.category_id)}
                              onChange={() => handleCategoryToggle(category.category_id)}
                            />
                          </Col>
                        ))}
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Selected: {applicationForm.selectedCategories.length}/3
                    </Form.Text>
                  </Form.Group>
                  
                  <Button 
                    type="submit" 
                    variant="success" 
                    size="lg"
                    disabled={submittingApplication}
                    className="w-100"
                  >
                    {submittingApplication ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Submitting Application...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {/* Add bottom margin to prevent footer overlap */}
      <div style={{ marginBottom: '100px' }}></div>
    </div>
  );

  return (
    <>
      <Header 
        showBackButton={view !== 'main'}
        onBackClick={() => {
          if (view === 'categories') setView('main');
          if (view === 'therapists') setView('categories');
          if (view === 'apply') setView('main');
        }}
        backText={
          view === 'categories' ? 'Main Menu' :
          view === 'therapists' ? 'Categories' :
          view === 'apply' ? 'Main Menu' : ''
        }
      />
      
      <div className="therapy-container">
        {view === 'main' && renderMainView()}
        {view === 'categories' && renderCategoriesView()}
        {view === 'therapists' && renderTherapistsView()}
        {view === 'apply' && renderApplicationView()}
      </div>
      
      {/* Session Request Modal */}
      <Modal show={showSessionModal} onHide={() => setShowSessionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Request Session with {selectedTherapist?.display_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sessionSuccess ? (
            <Alert variant="success">
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              Session request sent successfully! The therapist will review your request.
            </Alert>
          ) : (
            <Form onSubmit={handleSessionSubmit}>
              {sessionError && (
                <Alert variant="danger">
                  {sessionError}
                  {sessionError.includes('Insufficient balance') && (
                    <div className="mt-2">
                      <Link to="/wallet" className="btn btn-outline-light btn-sm">
                        <FontAwesomeIcon icon={faCoins} className="me-1" />
                        Add Funds to Wallet
                      </Link>
                    </div>
                  )}
                </Alert>
              )}
              
              <div className="mb-3">
                <strong>Price per Hour:</strong> 
                <div className="mt-2">
                  <Badge bg="info" className="me-2">
                    <FontAwesomeIcon icon={faCoins} className="me-1" />
                    {selectedTherapist?.price_per_session === 0 ? 'Free' : `${selectedTherapist?.price_per_session.toLocaleString()} coins/hour`}
                  </Badge>
                  <small className="text-muted d-none">
                    {selectedTherapist?.price_per_session > 0 && `(₦${(selectedTherapist?.price_per_session * 100).toLocaleString()}/hour)`}
                  </small>
                </div>
              </div>

              <div className="mb-3">
                <strong>Your Balance:</strong> 
                <div className="mt-2">
                  <Badge bg={userBalance >= sessionForm.totalPrice ? "success" : "danger"} className="me-2">
                    <FontAwesomeIcon icon={faCoins} className="me-1" />
                    {userBalance.toLocaleString()} coins
                  </Badge>
                  <small className="text-muted d-none">
                    (₦{(userBalance * 100).toLocaleString()})
                  </small>
                </div>
              </div>
              
              <div className="mb-3">
                <strong>Category:</strong> {selectedCategory?.category_name}
              </div>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Session Start Date & Time</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={sessionForm.startDateTime}
                      onChange={(e) => {
                        const newDateTime = e.target.value;
                        const newTotalPrice = calculateTotalPrice(sessionForm.duration, selectedTherapist?.price_per_session);
                        setSessionForm(prev => ({
                          ...prev,
                          startDateTime: newDateTime,
                          totalPrice: newTotalPrice
                        }));
                      }}
                      min={new Date().toISOString().slice(0, 16)}
                      required
                    />
                    <Form.Text className="text-muted">
                      Select when you'd like to start your session
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="8"
                      value={sessionForm.duration}
                      onChange={(e) => {
                        const newDuration = parseInt(e.target.value);
                        const newTotalPrice = calculateTotalPrice(newDuration, selectedTherapist?.price_per_session);
                        setSessionForm(prev => ({
                          ...prev,
                          duration: newDuration,
                          totalPrice: newTotalPrice
                        }));
                      }}
                      required
                    />
                    <Form.Text className="text-muted">
                      Session duration in hours (1-8 hours)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <div className="mb-3 p-3 bg-light rounded">
                <strong>Total Cost:</strong> 
                <div className="mt-2">
                  {sessionForm.totalPrice === 0 ? (
                    <Badge bg="success" className="me-2">
                      <FontAwesomeIcon icon={faCheck} className="me-1" />
                      Free Session
                    </Badge>
                  ) : (
                    <>
                      <Badge bg={userBalance >= sessionForm.totalPrice ? "success" : "danger"} className="me-2">
                        <FontAwesomeIcon icon={faCoins} className="me-1" />
                        {sessionForm.totalPrice.toLocaleString()} coins
                      </Badge>
                      <small className="text-muted d-none">
                        (₦{(sessionForm.totalPrice * 100).toLocaleString()})
                      </small>
                      {userBalance < sessionForm.totalPrice && (
                        <div className="text-danger small mt-1">
                          Insufficient balance. You need {sessionForm.totalPrice - userBalance} more coins.{' '}
                          <Link to="/wallet" className="text-primary text-decoration-none">
                            <FontAwesomeIcon icon={faCoins} className="me-1" />
                            Add funds to wallet
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>Describe your issue or concern</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Please provide details about what you'd like to discuss..."
                  value={sessionForm.issueText}
                  onChange={(e) => setSessionForm(prev => ({
                    ...prev,
                    issueText: e.target.value
                  }))}
                  required
                />
                <Form.Text className="text-muted">
                  Minimum 10 characters. Be as detailed as possible to help the therapist prepare.
                </Form.Text>
              </Form.Group>
              
              <div className="d-flex gap-2">
                <Button 
                  variant="secondary" 
                  onClick={() => setShowSessionModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={submittingSession}
                >
                  {submittingSession ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending Request...
                    </>
                  ) : (
                    'Send Request'
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
      
      <Footer />
    </>
  );
};

export default TherapyNew; 